class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.resultsResizeFrame = null;
    this.resultsResizeTargets = null;
    this.onResultsResize = this.scheduleResultsResize.bind(this);

    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.closeResults();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('[data-predictive-search-results-groups-wrapper]')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.abortController.abort();
        this.abortController = new AbortController();
        this.close(true);
        this.input.focus();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      const escapedPreviousTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const termMatches = currentButtonText.match(new RegExp(escapedPreviousTerm, 'g'));
      if (termMatches && termMatches.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);

    const results = this.predictiveSearchResults;
    if (results.scrollHeight > results.clientHeight) {
      const panelTop = results.getBoundingClientRect().top + results.clientTop;
      const panelBottom = panelTop + results.clientHeight;
      const option = activeElement.getBoundingClientRect();
      if (option.top < panelTop) {
        results.scrollTop += option.top - panelTop;
      } else if (option.bottom > panelBottom) {
        results.scrollTop += option.bottom - panelBottom;
      }
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    const requestTerm = searchTerm;
    this.setLiveRegionLoadingState();
    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      if (this.getQuery() === requestTerm) this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.getQuery() !== requestTerm) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  getNamespacedResultsMarkup(resultsMarkup) {
    const template = document.createElement('template');
    template.innerHTML = resultsMarkup;

    const namespace = this.input.id;
    const idMap = new Map();

    template.content.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      const namespacedId = `${namespace}--${originalId}`;
      idMap.set(originalId, namespacedId);
      element.id = namespacedId;
    });

    const idReferenceAttributes = [
      'aria-activedescendant',
      'aria-controls',
      'aria-describedby',
      'aria-details',
      'aria-errormessage',
      'aria-flowto',
      'aria-labelledby',
      'aria-owns',
      'for',
      'form',
      'headers',
      'list',
    ];

    idReferenceAttributes.forEach((attribute) => {
      template.content.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const references = element
          .getAttribute(attribute)
          .trim()
          .split(/\s+/)
          .filter(Boolean);

        if (!references.length) return;

        element.setAttribute(
          attribute,
          references.map((reference) => idMap.get(reference) || reference).join(' ')
        );
      });
    });

    return template.innerHTML;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = this.getNamespacedResultsMarkup(resultsMarkup);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const viewportHeight = Math.min(window.innerHeight, viewport?.height ?? window.innerHeight);
    const viewportBottom = viewport
      ? Math.min(window.innerHeight, viewport.offsetTop + viewport.height)
      : window.innerHeight;
    const panelTop = this.predictiveSearchResults.getBoundingClientRect().top;
    // Leave room for borders and fractional-pixel rounding at the viewport edge.
    this.resultsMaxHeight = Math.max(0, Math.floor(Math.min(viewportHeight, viewportBottom - panelTop)) - 2);
    return this.resultsMaxHeight;
  }

  updateResultsMaxHeight() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
  }

  scheduleResultsResize() {
    if (!this.isOpen || this.resultsResizeFrame !== null) return;
    this.resultsResizeFrame = requestAnimationFrame(() => {
      this.resultsResizeFrame = null;
      if (this.isOpen) this.updateResultsMaxHeight();
    });
  }

  startResultsResize() {
    if (this.resultsResizeTargets) return;
    this.resultsResizeTargets = [
      [window, 'resize'],
      [window, 'scroll'],
      [window.visualViewport, 'resize'],
      [window.visualViewport, 'scroll'],
      [this.closest('.section-header') || document.querySelector('.section-header'), 'transitionend'],
      [this.closest('.search-modal') || this.closest('.section-header'), 'animationend'],
    ].filter(([target]) => target);
    this.resultsResizeTargets.forEach(([target, event]) => {
      target.addEventListener(event, this.onResultsResize, { passive: true });
    });
  }

  stopResultsResize() {
    this.resultsResizeTargets?.forEach(([target, event]) => {
      target.removeEventListener(event, this.onResultsResize);
    });
    this.resultsResizeTargets = null;
    if (this.resultsResizeFrame !== null) cancelAnimationFrame(this.resultsResizeFrame);
    this.resultsResizeFrame = null;
  }

  open() {
    if (!this.isConnected) return;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.startResultsResize();
    this.updateResultsMaxHeight();
    this.scheduleResultsResize();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.isOpen = false;
    this.stopResultsResize();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
